function planThresholdGiftCartUpdates(items, tiers, propertyKey, tierPropertyKey) {
  const list = Array.isArray(items) ? items : [];
  const tierList = (Array.isArray(tiers) ? tiers : [])
    .map((tier) => ({
      id: Number(tier.id),
      cents: Number(tier.cents),
      mode: tier.mode === 'exclusive' ? 'exclusive' : 'independent',
      productIds: (Array.isArray(tier.productIds) ? tier.productIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    }))
    .filter((tier) => tier.id > 0);
  const catalogKnown = tierList.some((tier) => tier.productIds.length > 0);
  const productTierIds = new Map();
  tierList.forEach((tier) => {
    tier.productIds.forEach((productId) => {
      const current = productTierIds.get(productId) || [];
      current.push(tier.id);
      productTierIds.set(productId, current);
    });
  });
  const tierById = new Map(tierList.map((tier) => [tier.id, tier]));

  const eligibleSubtotal = list.reduce((sum, item) => {
    const productId = Number(item?.product_id);
    const marked = item?.properties?.[propertyKey] === '1';
    if (marked || productTierIds.has(productId)) return sum;
    return sum + Number(item?.final_line_price || 0);
  }, 0);
  const unlocked = new Set(tierList.filter((tier) => eligibleSubtotal >= tier.cents).map((tier) => tier.id));
  const updates = {};
  const keptByTier = new Map();

  list.forEach((item) => {
    if (!item?.key) return;
    const productId = Number(item.product_id);
    const catalogTierIds = productTierIds.get(productId) || [];
    const marked = item?.properties?.[propertyKey] === '1';
    if (!marked && catalogTierIds.length === 0) return;

    const claimedRaw = item?.properties?.[tierPropertyKey];
    let claimedTier = null;
    if (marked) {
      claimedTier = claimedRaw == null || claimedRaw === '' ? 1 : Number(claimedRaw);
    }
    const legacyGift = marked && catalogTierIds.length === 0 && !catalogKnown;
    const belongs = legacyGift || (marked && catalogTierIds.includes(claimedTier));
    const tier = belongs ? tierById.get(claimedTier) : null;
    if (!tier || !unlocked.has(tier.id)) {
      updates[item.key] = 0;
      return;
    }

    const group = keptByTier.get(tier.id) || [];
    group.push(item);
    keptByTier.set(tier.id, group);
  });

  keptByTier.forEach((group) => {
    group.slice(1).forEach((item) => {
      updates[item.key] = 0;
    });
    const keep = group[0];
    if (keep && updates[keep.key] == null && Number(keep.quantity) > 1) updates[keep.key] = 1;
  });

  const exclusive = [];
  keptByTier.forEach((group, tierId) => {
    const keep = group[0];
    if (!keep || updates[keep.key] === 0) return;
    if (tierById.get(tierId)?.mode === 'exclusive') exclusive.push({ tierId, key: keep.key });
  });
  exclusive.sort((a, b) => b.tierId - a.tierId);
  exclusive.slice(1).forEach((entry) => {
    updates[entry.key] = 0;
  });

  return { eligibleSubtotal, updates };
}

if (!customElements.get('tp-cart-threshold-gift')) {
  customElements.define(
    'tp-cart-threshold-gift',
    class CartThresholdGift extends HTMLElement {
      propertyKey = '_tp_threshold_gift';
      tierPropertyKey = '_tp_threshold_gift_tier';
      busy = false;
      syncing = false;
      tiers = [];

      connectedCallback() {
        this.propertyKey = this.dataset.propertyKey || '_tp_threshold_gift';
        this.tierPropertyKey = this.dataset.tierPropertyKey || '_tp_threshold_gift_tier';
        this.errorMessage = this.dataset.errorMessage || 'Could not update your gift. Please try again.';
        this.headingTemplate = this.dataset.headingTemplate || '{{ count }} FREE GIFTS UNLOCKED';
        this.lockedTemplate = this.dataset.lockedTemplate || 'Spend {{ amount }} more to unlock a free gift';
        this.errorEl = this.querySelector('[data-gift-error]');
        this.moneyFormat = window.FoxTheme?.settings?.moneyFormat;
        this.tiers = this.readTiers();
        this.renderDescription();
        this.onCartUpdated = this.onCartUpdated.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        this.addEventListener('click', this.onClick);
        this.querySelectorAll('[data-gift-variant]').forEach((input) => {
          input.addEventListener('change', this.onVariantChange);
          this.syncSelectButton(input);
        });

        document.addEventListener('cart:updated', this.onCartUpdated);
        this.bindCheckoutGuard();
        this.bindAddGuard();
        this.syncGiftEligibility();
      }

      disconnectedCallback() {
        document.removeEventListener('cart:updated', this.onCartUpdated);
        this.removeEventListener('click', this.onClick);
        this.querySelectorAll('[data-gift-variant]').forEach((input) => {
          input.removeEventListener('change', this.onVariantChange);
        });
      }

      readTiers() {
        const fromDom = [...this.querySelectorAll('[data-gift-tier]')].map((el) => {
          const amount = Number(el.dataset.tierAmount || 0);
          return {
            id: Number(el.dataset.tierId || 0),
            amount,
            cents: this.toPresentmentCents(amount),
            mode: el.dataset.tierMode === 'exclusive' ? 'exclusive' : 'independent',
            productIds: String(el.dataset.tierProductIds || '')
              .split(',')
              .map((id) => Number(id))
              .filter((id) => Number.isFinite(id) && id > 0),
            variantIds: String(el.dataset.tierVariantIds || '')
              .split(',')
              .map((id) => Number(id))
              .filter((id) => Number.isFinite(id) && id > 0),
            el,
          };
        });
        if (fromDom.length) return fromDom.filter((tier) => tier.id > 0 && tier.amount > 0);

        try {
          const jsonEl = this.querySelector('[data-gift-tiers]');
          const parsed = JSON.parse(jsonEl?.textContent || '[]');
          return (Array.isArray(parsed) ? parsed : [])
            .map((tier) => {
              const amount = Number(tier.amount || 0);
              return {
                id: Number(tier.id || 0),
                amount,
                cents: this.toPresentmentCents(amount),
                mode: tier.mode === 'exclusive' ? 'exclusive' : 'independent',
                productIds: (Array.isArray(tier.productIds) ? tier.productIds : [])
                  .map((id) => Number(id))
                  .filter((id) => Number.isFinite(id) && id > 0),
                variantIds: (Array.isArray(tier.variantIds) ? tier.variantIds : [])
                  .map((id) => Number(id))
                  .filter((id) => Number.isFinite(id) && id > 0),
                el: this.querySelector(`[data-gift-tier][data-tier-id="${tier.id}"]`),
              };
            })
            .filter((tier) => tier.id > 0 && tier.amount > 0);
        } catch (error) {
          console.error('Cart threshold gift tiers parse failed:', error);
          return [];
        }
      }

      getCurrencyRate() {
        const rate = Number(window.Shopify?.currency?.rate || 1);
        return Number.isFinite(rate) && rate > 0 ? rate : 1;
      }

      toPresentmentCents(shopAmount) {
        return Math.round(Number(shopAmount || 0) * this.getCurrencyRate() * 100);
      }

      formatMoney(cents) {
        const amount = Math.max(0, Math.round(Number(cents) || 0));
        if (window.FoxTheme?.Currency?.formatMoney && this.moneyFormat) {
          return window.FoxTheme.Currency.formatMoney(amount, this.moneyFormat);
        }
        return (amount / 100).toFixed(2);
      }

      /*
        描述里的 {{ 59 }} 是店铺货币金额（当前为美金）。
        按 Shopify.currency.rate 换成顾客当前货币后再格式化。
      */
      formatDescription(template) {
        return String(template || '').replace(/\{\{\s*(\d+(?:\.\d+)?)\s*\}\}/g, (_, shopAmount) => {
          return this.formatMoney(this.toPresentmentCents(shopAmount));
        });
      }

      renderDescription() {
        const el = this.querySelector('[data-gift-description]');
        const templateEl = this.querySelector('[data-gift-description-template]');
        if (!el || !templateEl) return;
        let template = '';
        try {
          template = JSON.parse(templateEl.textContent || '""');
        } catch (error) {
          template = el.textContent || '';
        }
        el.textContent = this.formatDescription(template);
      }

      onClick(event) {
        const addAllButton = event.target.closest('[data-gift-add-all]');
        if (addAllButton) {
          event.preventDefault();
          if (addAllButton.disabled || this.busy) return;
          this.addAllGifts(addAllButton);
          return;
        }
        const button = event.target.closest('[data-gift-select]');
        if (!button || button.disabled || this.busy) return;
        event.preventDefault();
        event.stopPropagation();
        const item = button.closest('[data-gift-item]');
        const variantInput = item?.querySelector('[data-gift-variant]');
        if (variantInput) this.syncSelectButton(variantInput);
        const variantId = Number(variantInput?.value || button.dataset.variantId || 0);
        const tierId = Number(button.dataset.tierId || item?.dataset.tierId || 0);
        if (!variantId || !tierId || button.disabled) {
          this.showError(this.errorMessage);
          return;
        }
        this.selectGift(variantId, tierId, button);
      }

      onVariantChange(event) {
        this.syncSelectButton(event.currentTarget);
      }

      syncSelectButton(variantInput) {
        const item = variantInput.closest('[data-gift-item]');
        const button = item?.querySelector('[data-gift-select]');
        if (!button) return;
        if (variantInput.tagName === 'SELECT') {
          const selected = variantInput.selectedOptions?.[0];
          if (!selected || selected.disabled) {
            const available = [...variantInput.options].find((option) => !option.disabled && option.value);
            if (available) variantInput.value = available.value;
          }
        }
        const variantId = variantInput.value;
        button.dataset.variantId = variantId;
        const option = variantInput.selectedOptions?.[0];
        const tierUnlocked = item?.closest('[data-gift-tier]')?.dataset.tierUnlocked !== 'false';
        button.disabled = !tierUnlocked || !variantId || Boolean(option?.disabled);
      }

      onCartUpdated(event) {
        const cart = event?.detail?.cart;
        if (!cart?.items || typeof cart.item_count !== 'number') return;
        this.syncGiftEligibility(cart);
      }

      isCatalogGift(item) {
        const productId = Number(item?.product_id);
        return this.tiers.some((tier) => (tier.productIds || []).includes(productId));
      }

      getEligibleSubtotal(cart) {
        if (!cart?.items) return Number(this.dataset.eligibleSubtotal || 0);
        return cart.items.reduce((sum, item) => {
          if (this.isGiftItem(item) || this.isCatalogGift(item)) return sum;
          return sum + Number(item.final_line_price || 0);
        }, 0);
      }

      isGiftItem(item) {
        return item?.properties?.[this.propertyKey] === '1';
      }

      getGiftTierId(item) {
        const raw = item?.properties?.[this.tierPropertyKey];
        if (raw == null || raw === '') return 1;
        const tierId = Number(raw);
        return Number.isFinite(tierId) && tierId > 0 ? tierId : 1;
      }

      getGiftLines(cart) {
        if (!cart?.items) return [];
        return cart.items.filter((item) => this.isGiftItem(item));
      }

      getGiftLineKeys(cart, predicate = null) {
        return this.getGiftLines(cart)
          .filter((item) => (typeof predicate === 'function' ? predicate(item) : true))
          .map((item) => item.key)
          .filter(Boolean);
      }

      getUnlockedTierIds(eligibleSubtotal) {
        return this.tiers.filter((tier) => eligibleSubtotal >= tier.cents).map((tier) => tier.id);
      }

      getTierById(tierId) {
        return this.tiers.find((tier) => tier.id === Number(tierId));
      }

      keysToRemoveForSelect(cart, tierId) {
        const tier = this.getTierById(tierId);
        if (!tier) return [];

        if (tier.mode === 'exclusive') {
          return this.getGiftLineKeys(cart, (item) => {
            const itemTier = this.getGiftTierId(item);
            if (itemTier === tierId) return true;
            return this.getTierById(itemTier)?.mode === 'exclusive';
          });
        }

        return this.getGiftLineKeys(cart, (item) => this.getGiftTierId(item) === tierId);
      }

      getGuardedVariantIds() {
        const ids = new Set();
        this.tiers.forEach((tier) => {
          (tier.variantIds || []).forEach((id) => ids.add(Number(id)));
        });
        return ids;
      }

      lineHasGiftProperty(properties) {
        if (!properties || typeof properties !== 'object') return false;
        if (properties[this.propertyKey] === '1') return true;
        if (properties[`properties[${this.propertyKey}]`] === '1') return true;
        return false;
      }

      collectAddPayloadLines(payload) {
        if (!payload) return [];
        if (Array.isArray(payload.items)) return payload.items;
        if (payload.id != null) return [payload];
        return [];
      }

      parseAddRequestBody(body) {
        if (body == null || body === '') return null;
        if (typeof body === 'string') {
          try {
            return JSON.parse(body);
          } catch (error) {
            const params = new URLSearchParams(body);
            const items = [];
            const singleId = params.get('id') || params.get('items[][id]');
            if (singleId) {
              const properties = {};
              params.forEach((value, key) => {
                const match = key.match(/^properties\[(.+)\]$/);
                if (match) properties[match[1]] = value;
              });
              items.push({ id: singleId, properties });
            }
            return items.length ? { items } : null;
          }
        }
        if (typeof FormData !== 'undefined' && body instanceof FormData) {
          const items = [];
          const singleId = body.get('id') || body.get('items[][id]');
          if (singleId) {
            const properties = {};
            body.forEach((value, key) => {
              const match = String(key).match(/^properties\[(.+)\]$/);
              if (match) properties[match[1]] = value;
            });
            items.push({ id: singleId, properties });
          }
          const nested = {};
          body.forEach((value, key) => {
            const match = String(key).match(/^items\[(\d+)\]\[(.+)\]$/);
            if (!match) return;
            const index = match[1];
            nested[index] = nested[index] || { properties: {} };
            if (match[2] === 'id') nested[index].id = value;
            else if (match[2].startsWith('properties[')) {
              const prop = match[2].match(/^properties\[(.+)\]$/);
              if (prop) nested[index].properties[prop[1]] = value;
            }
          });
          Object.values(nested).forEach((item) => items.push(item));
          return items.length ? { items } : null;
        }
        if (typeof body === 'object') return body;
        return null;
      }

      isBlockedGiftAdd(payload) {
        const guarded = this.getGuardedVariantIds();
        if (!guarded.size) return false;
        return this.collectAddPayloadLines(payload).some((line) => {
          const variantId = Number(line?.id || line?.variant_id || 0);
          if (!variantId || !guarded.has(variantId)) return false;
          return !this.lineHasGiftProperty(line.properties);
        });
      }

      blockedGiftAddResponse() {
        const message = this.errorMessage || 'This gift can only be added from Choose free gifts.';
        return new Response(
          JSON.stringify({
            status: 422,
            message,
            description: message,
          }),
          {
            status: 422,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          }
        );
      }

      bindAddGuard() {
        if (window.__tpThresholdGiftAddGuard) return;
        window.__tpThresholdGiftAddGuard = true;
        const originalFetch = window.fetch.bind(window);
        window.fetch = async (input, init = {}) => {
          const url = typeof input === 'string' ? input : input?.url || '';
          if (/\/cart\/add(?:\.js)?(?:\?|$)/i.test(String(url))) {
            const host = document.querySelector('tp-cart-threshold-gift');
            if (host?.isBlockedGiftAdd) {
              host.tiers = host.readTiers?.() || host.tiers || [];
              const payload = host.parseAddRequestBody(init?.body);
              if (host.isBlockedGiftAdd(payload)) return host.blockedGiftAddResponse();
            }
          }
          return originalFetch(input, init);
        };

        document.addEventListener(
          'submit',
          (event) => {
            const form = event.target;
            if (!(form instanceof HTMLFormElement)) return;
            const action = form.getAttribute('action') || '';
            if (!/\/cart\/add/i.test(action)) return;
            const host = document.querySelector('tp-cart-threshold-gift');
            if (!host?.isBlockedGiftAdd) return;
            host.tiers = host.readTiers?.() || host.tiers || [];
            const payload = host.parseAddRequestBody(new FormData(form));
            if (!host.isBlockedGiftAdd(payload)) return;
            event.preventDefault();
            event.stopPropagation();
            host.showError?.(host.errorMessage);
          },
          true
        );
      }

      bindCheckoutGuard() {
        if (window.__tpThresholdGiftCheckoutGuard) return;
        window.__tpThresholdGiftCheckoutGuard = true;
        document.addEventListener(
          'submit',
          (event) => {
            const form = event.target;
            const submitter = event.submitter;
            if (!(form instanceof HTMLFormElement) || submitter?.name !== 'checkout') return;
            const host = document.querySelector('tp-cart-threshold-gift');
            if (!host?.prepareCheckout) return;
            event.preventDefault();
            event.stopPropagation();
            submitter.disabled = true;
            host.prepareCheckout(form).catch(() => {
              submitter.disabled = false;
              host.showError(host.errorMessage);
            });
          },
          true
        );
      }

      async prepareCheckout(form) {
        await this.syncGiftEligibility();
        const note = form?.querySelector('[name="note"]');
        if (note && this.dataset.updateUrl) {
          try {
            await fetch(this.dataset.updateUrl, {
              ...this.requestConfig(),
              body: JSON.stringify({ note: note.value }),
            });
          } catch (error) {
            console.error('Cart note save before checkout failed:', error);
          }
        }
        window.location.assign(this.dataset.checkoutUrl || '/checkout');
      }

      async syncGiftEligibility(cart) {
        if (this.busy) return;
        if (window.__tpThresholdGiftSync) return window.__tpThresholdGiftSync;
        const run = this.runGiftSync(cart).finally(() => {
          if (window.__tpThresholdGiftSync === run) window.__tpThresholdGiftSync = null;
        });
        window.__tpThresholdGiftSync = run;
        return run;
      }

      async runGiftSync(cart) {
        if (this.syncing) return;
        let currentCart = cart;
        this.syncing = true;
        try {
          if (!currentCart?.items) currentCart = await this.fetchCart();
          this.tiers = this.readTiers();
          const plan = planThresholdGiftCartUpdates(
            currentCart.items,
            this.tiers,
            this.propertyKey,
            this.tierPropertyKey
          );
          const eligibleSubtotal = plan.eligibleSubtotal;
          const unlocked = this.getUnlockedTierIds(eligibleSubtotal);
          const giftKeys = this.getGiftLineKeys(currentCart);
          this.dataset.eligibleSubtotal = String(eligibleSubtotal);
          this.dataset.eligible = unlocked.length ? 'true' : 'false';
          this.dataset.hasGift = giftKeys.length ? 'true' : 'false';
          this.renderTierState(eligibleSubtotal, unlocked);
          this.renderFooterState(eligibleSubtotal, unlocked.length, giftKeys.length > 0);

          if (Object.keys(plan.updates).length) await this.applyCartUpdates(plan.updates);
        } catch (error) {
          console.error('Cart threshold gift sync failed:', error);
        } finally {
          this.syncing = false;
        }
      }

      renderFooterState(eligibleSubtotal, unlockedCount, hasGift) {
        const isEligible = Number(unlockedCount || 0) > 0;
        const unlockedEl = this.querySelector('[data-gift-message-unlocked]');
        const lockedEl = this.querySelector('[data-gift-message-locked]');
        const banner = this.querySelector('[data-gift-banner]');
        const trigger = this.querySelector('[data-gift-trigger]');
        const triggerLabel = this.querySelector('[data-gift-trigger-label]');

        unlockedEl?.classList.toggle('hidden', !isEligible);
        lockedEl?.classList.toggle('hidden', isEligible);
        banner?.classList.toggle('is-locked', !isEligible);

        if (isEligible && unlockedEl) {
          unlockedEl.textContent = this.headingTemplate.replace(/\{\{\s*count\s*\}\}/g, String(unlockedCount));
        }

        if (!isEligible && lockedEl) {
          const nextTier = this.tiers
            .slice()
            .sort((a, b) => a.cents - b.cents)
            .find((tier) => eligibleSubtotal < tier.cents);
          const remaining = nextTier ? this.formatMoney(nextTier.cents - eligibleSubtotal) : '';
          lockedEl.textContent = this.lockedTemplate.replace(/\{\{\s*amount\s*\}\}/g, remaining);
        }

        if (trigger) {
          trigger.classList.toggle('hidden', !isEligible);
          trigger.hidden = !isEligible;
        }
        if (triggerLabel) {
          const chooseLabel = this.dataset.buttonLabel;
          const changeLabel = this.dataset.changeLabel;
          if (hasGift && changeLabel) triggerLabel.textContent = changeLabel;
          else if (chooseLabel) triggerLabel.textContent = chooseLabel;
        }
      }

      renderTierState(eligibleSubtotal, unlockedIds) {
        const unlockedSet = new Set(unlockedIds);
        this.tiers.forEach((tier) => {
          const tierEl = tier.el || this.querySelector(`[data-gift-tier][data-tier-id="${tier.id}"]`);
          if (!tierEl) return;
          const unlocked = unlockedSet.has(tier.id);
          tierEl.classList.toggle('is-unlocked', unlocked);
          tierEl.classList.toggle('is-locked', !unlocked);
          tierEl.dataset.tierUnlocked = unlocked ? 'true' : 'false';

          const statusEl = tierEl.querySelector('[data-tier-status]');
          if (statusEl) {
            statusEl.classList.toggle('is-unlocked', unlocked);
            statusEl.classList.toggle('is-locked', !unlocked);
            statusEl.textContent = `≥ ${this.formatMoney(tier.cents)}`;
          }

          tierEl.querySelectorAll('[data-gift-variant]').forEach((input) => {
            if (input.tagName === 'SELECT' || input.tagName === 'INPUT') {
              input.disabled = !unlocked;
            }
            this.syncSelectButton(input);
          });
          tierEl.querySelectorAll('[data-gift-select]').forEach((button) => {
            if (!unlocked) button.disabled = true;
          });
        });
      }

      async selectGift(variantId, tierId, button) {
        this.busy = true;
        this.clearError();
        this.setLoading(button, true);
        try {
          const cart = await this.fetchCart();
          const eligibleSubtotal = this.getEligibleSubtotal(cart);
          const tier = this.getTierById(tierId);
          if (!tier || eligibleSubtotal < tier.cents) {
            throw new Error(this.errorMessage);
          }

          const alreadySelected = cart.items?.some(
            (item) =>
              this.isGiftItem(item) &&
              this.getGiftTierId(item) === Number(tierId) &&
              Number(item.variant_id) === Number(variantId)
          );
          if (alreadySelected) {
            this.closeModal();
            return;
          }

          const removeKeys = this.keysToRemoveForSelect(cart, tierId);
          if (removeKeys.length) {
            await this.removeGiftKeys(removeKeys, { publish: false });
          }

          await this.addGift(variantId, tierId);
          this.closeModal();
        } catch (error) {
          this.showError(error instanceof TypeError ? this.errorMessage : error.message || this.errorMessage);
        } finally {
          this.setLoading(button, false);
          this.busy = false;
        }
      }

      collectAddAllItems() {
        const chosen = [];
        const exclusive = [];
        this.querySelectorAll('[data-gift-tier]').forEach((tierEl) => {
          if (tierEl.dataset.tierUnlocked === 'false') return;
          const tierId = Number(tierEl.dataset.tierId || 0);
          if (!tierId) return;
          const item = tierEl.querySelector('[data-gift-item]');
          const input = item?.querySelector('[data-gift-variant]');
          const variantId = Number(input?.value || 0);
          if (!variantId || input?.disabled || input?.selectedOptions?.[0]?.disabled) return;
          const entry = {
            tierId,
            variantId,
            mode: tierEl.dataset.tierMode === 'exclusive' ? 'exclusive' : 'independent',
          };
          if (entry.mode === 'exclusive') exclusive.push(entry);
          else chosen.push(entry);
        });
        if (exclusive.length) {
          exclusive.sort((a, b) => b.tierId - a.tierId);
          chosen.push(exclusive[0]);
        }
        return chosen;
      }

      async addAllGifts(button) {
        const items = this.collectAddAllItems();
        if (!items.length) {
          this.showError(this.errorMessage);
          return;
        }
        this.busy = true;
        this.clearError();
        this.setLoading(button, true);
        try {
          const cart = await this.fetchCart();
          const eligibleSubtotal = this.getEligibleSubtotal(cart);
          const toAdd = [];
          const removeKeys = new Set();
          items.forEach(({ tierId, variantId }) => {
            const tier = this.getTierById(tierId);
            if (!tier || eligibleSubtotal < tier.cents) return;
            const alreadySelected = cart.items?.some(
              (item) =>
                this.isGiftItem(item) &&
                this.getGiftTierId(item) === Number(tierId) &&
                Number(item.variant_id) === Number(variantId)
            );
            if (alreadySelected) return;
            this.keysToRemoveForSelect(cart, tierId).forEach((key) => removeKeys.add(key));
            toAdd.push({ tierId, variantId });
          });
          if (removeKeys.size) await this.removeGiftKeys([...removeKeys], { publish: false });
          if (toAdd.length) await this.addGifts(toAdd);
          this.closeModal();
        } catch (error) {
          this.showError(error instanceof TypeError ? this.errorMessage : error.message || this.errorMessage);
        } finally {
          this.setLoading(button, false);
          this.busy = false;
        }
      }

      async addGift(variantId, tierId) {
        return this.addGifts([{ variantId, tierId }]);
      }

      async addGifts(entries) {
        const sections = this.getSectionsToUpdate();
        const response = await fetch(this.dataset.addUrl, {
          ...this.requestConfig(),
          body: JSON.stringify({
            items: entries.map(({ variantId, tierId }) => ({
              id: variantId,
              quantity: 1,
              properties: {
                [this.propertyKey]: '1',
                [this.tierPropertyKey]: String(tierId),
              },
            })),
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const result = await this.readJson(response);
        if (result.status) {
          throw new Error(typeof result.description === 'string' ? result.description : this.errorMessage);
        }
        const cart = await this.fetchCart();
        cart.sections = result.sections || {};
        this.publishCartUpdate(cart);
      }

      async removeGiftKeys(keys, { publish = true } = {}) {
        if (!keys.length) return null;
        const updates = {};
        keys.forEach((key) => {
          updates[key] = 0;
        });
        return this.applyCartUpdates(updates, { publish });
      }

      async applyCartUpdates(updates, { publish = true } = {}) {
        if (!updates || !Object.keys(updates).length) return null;
        const sections = this.getSectionsToUpdate();
        const response = await fetch(this.dataset.updateUrl, {
          ...this.requestConfig(),
          body: JSON.stringify({ updates, sections }),
        });
        const result = await this.readJson(response);
        if (result.status) {
          throw new Error(typeof result.description === 'string' ? result.description : this.errorMessage);
        }
        if (publish) {
          if (typeof result.item_count !== 'number') {
            const cart = await this.fetchCart();
            cart.sections = result.sections || {};
            this.publishCartUpdate(cart);
          } else {
            this.publishCartUpdate(result);
          }
        }
        return result;
      }

      requestConfig() {
        const themeConfig = window.FoxTheme?.utils?.fetchConfig?.('javascript');
        return {
          method: 'POST',
          credentials: 'same-origin',
          headers: themeConfig?.headers || {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          },
        };
      }

      async fetchCart() {
        const response = await fetch(this.dataset.cartUrl, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        return this.readJson(response);
      }

      async readJson(response) {
        const contentType = response.headers.get('Content-Type') || '';
        const isJson = /(?:application|text)\/(?:[\w.+-]*\+)?(?:json|javascript)\b/i.test(contentType);
        if (!response.ok) {
          const error = isJson ? await response.json().catch(() => null) : null;
          throw new Error(typeof error?.description === 'string' ? error.description : this.errorMessage);
        }
        if (!isJson) throw new Error(this.errorMessage);
        return response.json();
      }

      getSectionsToUpdate() {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );
        return [...new Set(sections)].slice(0, 5);
      }

      publishCartUpdate(cart) {
        const pubsub = window.FoxTheme?.pubsub;
        if (!pubsub?.publish || !pubsub?.PUB_SUB_EVENTS?.cartUpdate) return;
        pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { cart, sources: 'cart-threshold-gift' });
      }

      closeModal() {
        const trigger = this.querySelector('[data-gift-trigger]');
        const modalId = trigger?.getAttribute('aria-controls');
        const modal = modalId ? document.getElementById(modalId) : this.querySelector('modal-component');
        if (typeof modal?.hide === 'function') {
          modal.hide();
        } else if (modal) {
          modal.hidden = true;
          modal.removeAttribute('active');
        }
      }

      setLoading(button, loading) {
        if (!button) return;
        button.classList.toggle('btn--loading', loading);
        button.setAttribute('aria-busy', loading ? 'true' : 'false');
        button.disabled = loading;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.remove('hidden');
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.classList.add('hidden');
      }
    }
  );
}
